/*
 * PES Search — Riven Search (Meilisearch) provider
 * ============================================================
 * Loaded by layout/theme.liquid ONLY when settings.search_provider == 'riven'.
 * Rollback: set theme setting search_provider back to 'shopify' — this file
 * then never loads and the theme behaves exactly as before.
 *
 * Two surfaces:
 *  1. Predictive drawer — patches PredictiveSearch.prototype.getSearchResults
 *     to query the Riven index and render markup that matches
 *     sections/predictive-search.liquid (same classes/CSS). On any error or
 *     timeout it silently calls the original Shopify-suggest implementation.
 *  2. /search results page — queries the Riven index and swaps the product
 *     grid, count line and pagination. Server-rendered Shopify results stay
 *     in the DOM until Riven answers; on error/timeout/zero-hits the native
 *     page is left untouched (graceful degradation).
 *
 * The embedded API key is a Meilisearch SEARCH-ONLY key (actions: ["search"],
 * indexes: ["products"]) — safe to expose publicly by design.
 * ============================================================
 */
(function () {
  'use strict';

  var RIVEN_HOST = 'https://meilisearch.wittydesert-8871d1af.eastus.azurecontainerapps.io';
  var RIVEN_INDEX = 'products';
  var RIVEN_KEY = 'ff74dc864e73937dd6a938ec07535419a78ecc3f66ac2cd1eec36394b2f50055';
  var TIMEOUT_MS = 2500;
  var DRAWER_LIMIT = 6;
  var PAGE_SIZE = 24;

  /* ---------- tiny utils ---------- */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(n) {
    var v = parseFloat(n);
    if (isNaN(v)) return '';
    return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' USD';
  }

  /* ---------- stock truth table (mirrors card-product.liquid 2026-10 wave) ---------- */

  function stockOf(hit) {
    var qty = typeof hit.inventory_qty === 'number' ? hit.inventory_qty : 0;
    if (hit.availability === 'in_stock') {
      if (qty >= 1 && qty <= 5) {
        return { state: 'low', cardLabel: 'Only ' + qty + ' left', corner: 'In Stock — ' + qty + ' left', ship: 'Ships in 1–2 business days' };
      }
      if (qty >= 6 && qty <= 10) {
        return { state: 'in', cardLabel: 'In stock — ' + qty + ' left', corner: 'In Stock — ' + qty + ' left', ship: 'Ships in 1–2 business days' };
      }
      return { state: 'in', cardLabel: 'In stock', corner: 'In Stock', ship: 'Ships in 1–2 business days' };
    }
    if (hit.availability === 'out_of_stock') {
      return { state: 'out', cardLabel: 'Out of stock', corner: 'Sold Out — Call or Email', ship: '' };
    }
    return { state: 'order', cardLabel: 'Available to order', corner: 'Available to Order', ship: 'Lead time confirmed at checkout' };
  }

  function drawerBadge(hit) {
    var s = stockOf(hit);
    var text = s.state === 'out' ? 'Sold Out — Call/Email' : s.corner;
    return '<span class="pes-ps-badge pes-ps-badge--' + s.state + '">' + esc(text) + '</span>';
  }

  /* ---------- styles (drawer badge + card chrome that normally ships inside card-product.liquid) ---------- */

  /* Card inline styles copied 1:1 from snippets/card-product.liquid's inline
     <style> block (they normally render per-card; our JS-rendered cards need
     them injected once). */
  var CARD_CSS =
    '.pes-stock-state{display:inline-flex;align-items:center;gap:6px;margin-top:6px;font-size:12.5px;font-weight:600;line-height:1.3}' +
    '.pes-stock-state .pes-stock-dot{width:9px;height:9px;border-radius:50%;flex:0 0 auto}' +
    '.pes-stock-state--in{color:#1e7a46}.pes-stock-state--in .pes-stock-dot{background:#22a355}' +
    '.pes-stock-state--out{color:#b3261e}.pes-stock-state--out .pes-stock-dot{background:#d42b21}' +
    '.pes-stock-state--low{color:#9a6212}.pes-stock-state--low .pes-stock-dot{background:#f0a92a}' +
    '.pes-stock-state--order{color:#47525e}.pes-stock-state--order .pes-stock-dot{background:#8a97a5}' +
    '.pes-card-brand{font-size:11px;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;color:#5f6f68;line-height:1.2;margin:0 0 2px}' +
    '.card__content .price .price-item--regular,.card__content .price .price-item--sale{color:#0d9a68;font-weight:700}' +
    '.card__content .price s.price-item--regular{color:#98a2b3;font-weight:500}' +
    '.pes-card-ship{font-size:12px;font-weight:500;color:#47525e;margin-top:2px;line-height:1.3}' +
    '.card-wrapper .pes-status-chip{font-size:9.5px;font-weight:600;padding:2px 7px;margin-left:6px;letter-spacing:0.3px}' +
    '.card-wrapper .pes-status-chip--legacy{background:transparent;border-color:#d3dae0;color:#8a97a5}' +
    '.card__media .media img{padding:12px;box-sizing:border-box;background:#fff}' +
    /* pes-oos-ctas button chrome (snippet CSS only ships with server-rendered cards) */
    '.pes-oos-ctas{display:flex;gap:8px;margin-top:10px}' +
    '.pes-oos-btn{flex:1 1 auto;text-align:center;padding:8px 10px;border-radius:6px;font-size:12.5px;font-weight:700;text-decoration:none}' +
    '.pes-oos-btn--call{background:#0f1e2d;color:#fff}' +
    '.pes-oos-btn--email{background:#fff;color:#0f1e2d;border:2px solid #0f1e2d}';

  function injectStyles() {
    if (document.getElementById('pes-riven-search-css')) return;
    var css = CARD_CSS +
      '.pes-ps-badge{display:inline-block;margin-top:3px;padding:2px 7px;border-radius:4px;' +
        'font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;line-height:1.4}' +
      '.pes-ps-badge--in{background:#e7f7ef;color:#0d9a68}' +
      '.pes-ps-badge--order{background:#eef1f5;color:#47525e}' +
      '.pes-ps-badge--out{background:#fdecea;color:#c0392b}' +
      '.pes-riven-note{display:none}';
    var el = document.createElement('style');
    el.id = 'pes-riven-search-css';
    el.textContent = css;
    document.head.appendChild(el);
  }

  /* ---------- image resolution (index carries no image; products.js is CDN-cheap) ---------- */

  var imgMemCache = {};
  var IMG_SS_KEY = 'pesRivenImgV1';

  function imgSessionGet() {
    try { return JSON.parse(sessionStorage.getItem(IMG_SS_KEY) || '{}'); } catch (e) { return {}; }
  }
  function imgSessionMerge(map) {
    try {
      var cur = imgSessionGet();
      for (var k in map) cur[k] = map[k];
      sessionStorage.setItem(IMG_SS_KEY, JSON.stringify(cur));
    } catch (e) { /* private mode */ }
  }

  function fetchProductImage(handle, attempt) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) {
        if (r.status === 429 && !attempt) {
          return new Promise(function (res) { setTimeout(res, 700); })
            .then(function () { return fetchProductImage(handle, 1); });
        }
        return r.ok ? r.json() : null;
      })
      .then(function (p) { return (p && (p.featured_image || (p.images && p.images[0]))) || ''; })
      .catch(function () { return ''; });
  }

  function resolveImages(hits) {
    var session = imgSessionGet();
    var need = [];
    hits.forEach(function (h) {
      if (h.handle in imgMemCache) { h._img = imgMemCache[h.handle]; return; }
      if (h.handle in session) { imgMemCache[h.handle] = session[h.handle]; h._img = session[h.handle]; return; }
      need.push(h);
    });
    if (!need.length) return Promise.resolve(hits);
    // concurrency-limited pool (products.js 429s when 24 fire at once)
    var idx = 0;
    var POOL = 6;
    function worker() {
      if (idx >= need.length) return Promise.resolve();
      var h = need[idx++];
      return fetchProductImage(h.handle).then(function (img) {
        h._img = img;
        imgMemCache[h.handle] = img;
        var m = {}; m[h.handle] = img;
        imgSessionMerge(m);
      }).then(worker);
    }
    var workers = [];
    for (var i = 0; i < Math.min(POOL, need.length); i++) workers.push(worker());
    return Promise.all(workers).then(function () { return hits; });
  }

  function imgUrl(raw, width) {
    if (!raw) return '';
    var u = raw.indexOf('//') === 0 ? 'https:' + raw : raw;
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ---------- riven query (null = fallback to native) ---------- */

  function rivenQuery(q, limit, offset) {
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS);
    var t0 = (window.performance && performance.now) ? performance.now() : 0;
    return fetch(RIVEN_HOST + '/indexes/' + RIVEN_INDEX + '/search', {
      method: 'POST',
      signal: ctrl.signal,
      headers: { 'Authorization': 'Bearer ' + RIVEN_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        q: q,
        limit: limit,
        offset: offset || 0,
        attributesToRetrieve: ['id', 'handle', 'url', 'title', 'vendor', 'sku', 'mpn', 'price', 'availability', 'inventory_qty']
      })
    }).then(function (r) {
      clearTimeout(timer);
      if (!r.ok) return null;
      return r.json().then(function (d) {
        if (t0) window.__pesRivenLastMs = Math.round(performance.now() - t0);
        return d;
      });
    }).catch(function () {
      clearTimeout(timer);
      return null;
    });
  }

  /* ---------- price markup (same shape as snippets/price.liquid render) ---------- */

  var PRICE_STYLE = '<style>' +
    '.pes-sale-badge{display:inline-block;margin-left:8px;padding:3px 8px;border-radius:4px;background:#cc0d39;color:#fff;font-size:12px;font-weight:700;line-height:1.2;vertical-align:middle}' +
    '.price--large .price-item--regular,.price--large .price-item--sale{font-size:30px !important;font-weight:700 !important;color:#0f1e2d;line-height:1.15}' +
    '.price--large s.price-item--regular{font-size:18px !important;font-weight:500 !important}' +
    '.price--large .custm-panel-price-wt{font-size:19px !important;font-weight:600 !important;color:#0f1e2d}' +
    '</style>';

  function priceHtml(hit) {
    var p = money(hit.price);
    return '<div class="price"><div class="price__container">' +
      '<div class="price__regular cust-prwtprice">' +
        '<span class="visually-hidden visually-hidden--inline">Regular price</span>' +
        '<span class="price-item price-item--regular">' + esc(p) + '</span>' +
      '</div>' +
      '<div class="price__sale">' +
        '<span class="visually-hidden visually-hidden--inline">Regular price</span>' +
        '<span><s class="price-item price-item--regular"></s></span>' +
        '<span class="visually-hidden visually-hidden--inline">Sale price</span>' +
        '<span class="price-item price-item--sale price-item--last">' + esc(p) + '</span>' +
      '</div>' +
      '<small class="unit-price caption hidden">' +
        '<span class="visually-hidden">Unit price</span>' +
        '<span class="price-item price-item--last"><span></span><span aria-hidden="true">/</span>' +
        '<span class="visually-hidden">&nbsp;per&nbsp;</span><span></span></span>' +
      '</small>' +
    '</div></div>' + PRICE_STYLE;
  }

  /* ---------- 1. predictive drawer ---------- */

  var SPINNER = '<div class="predictive-search__loading-state">' +
    '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">' +
    '<circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg></div>';

  var ARROW = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-arrow" viewBox="0 0 14 10">' +
    '<path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"/></svg>';

  function searchForButton(term) {
    return '<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">' +
      '<button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">' +
        '<span data-predictive-search-search-for-text>Search for &ldquo;' + esc(term) + '&rdquo;</span>' +
        '<span class="svg-wrapper">' + ARROW + '</span>' +
      '</button></div>';
  }

  function drawerMarkup(term, hits) {
    var items = '';
    hits.forEach(function (h, i) {
      var img = h._img
        ? '<img class="predictive-search__image" src="' + esc(imgUrl(h._img, 150)) + '" alt="' + esc(h.title) + '" width="50" height="50" loading="lazy">'
        : '';
      items += '<li id="predictive-search-option-product-' + (i + 1) + '" class="predictive-search__list-item" role="option" aria-selected="false">' +
        '<a href="' + esc(h.url || ('/products/' + h.handle)) + '" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">' +
          img +
          '<div class="predictive-search__item-content">' +
            '<p class="predictive-search__item-heading h5">' + esc(h.title) + '</p>' +
            priceHtml(h) +
            drawerBadge(h) +
          '</div>' +
        '</a></li>';
    });

    var inner;
    if (hits.length) {
      inner = '<div id="predictive-search-results" role="listbox">' +
        '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">' +
          '<div class="predictive-search__result-group"><div>' +
            '<h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>' +
            '<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">' +
              items +
            '</ul>' +
          '</div></div>' +
        '</div>' +
        SPINNER +
        searchForButton(term) +
        '</div>' +
        '<span class="hidden" data-predictive-search-live-region-count-value>' +
          hits.length + ' results: ' + hits.length + ' products' +
        '</span>';
    } else {
      inner = '<div id="predictive-search-results" role="listbox">' +
        SPINNER +
        searchForButton(term) +
        '</div>' +
        '<span class="hidden" data-predictive-search-live-region-count-value>' +
          'No results found for &ldquo;' + esc(term) + '&rdquo;.' +
        '</span>';
    }
    return inner;
  }

  function patchPredictive() {
    var PS = (typeof PredictiveSearch !== 'undefined') ? PredictiveSearch
           : (window.customElements && customElements.get('predictive-search'));
    if (!PS || !PS.prototype) return false;
    if (PS.prototype.__pesRivenPatched) return true;
    PS.prototype.__pesRivenPatched = true;

    var origGetSearchResults = PS.prototype.getSearchResults;

    PS.prototype.getSearchResults = function (searchTerm) {
      var self = this;
      var queryKey = searchTerm.replace(' ', '-').toLowerCase();
      self.setLiveRegionLoadingState();

      if (self.cachedResults[queryKey]) {
        self.renderSearchResults(self.cachedResults[queryKey]);
        return;
      }

      var seq = (self._pesRivenSeq = (self._pesRivenSeq || 0) + 1);

      rivenQuery(searchTerm, DRAWER_LIMIT, 0).then(function (res) {
        if (self._pesRivenSeq !== seq) return; // stale
        if (res === null) {
          // silent fallback to Shopify suggest (original implementation)
          origGetSearchResults.call(self, searchTerm);
          return;
        }
        var hits = res.hits || [];
        // paint immediately without images; re-render with images when resolved
        self.renderSearchResults(drawerMarkup(searchTerm, hits));
        resolveImages(hits).then(function () {
          if (self._pesRivenSeq !== seq) return; // stale
          var markup = drawerMarkup(searchTerm, hits);
          self.allPredictiveSearchInstances.forEach(function (inst) {
            inst.cachedResults[queryKey] = markup;
          });
          self.renderSearchResults(markup);
        });
      });
    };
    return true;
  }

  /* ---------- 2. /search results page ---------- */

  function cardHtml(hit) {
    var s = stockOf(hit);
    var url = hit.url || ('/products/' + hit.handle);
    var p = money(hit.price);
    var img = hit._img
      ? '<a class="card_media_effect" href="' + esc(url) + '">' +
        '<img src="' + esc(imgUrl(hit._img, 533)) + '" alt="' + esc(hit.title) + '" class="motion-reduce" width="900" height="900" loading="lazy"></a>'
      : '';
    var badge = hit.availability !== 'out_of_stock'
      ? '<div class="card__badge bottom left"><span class="badge pes-oos-badge-in">In Stock</span></div>'
      : '<div class="card__badge bottom left"></div>';
    var oosCtas = hit.availability === 'out_of_stock'
      ? '<div class="pes-oos-ctas pes-oos-ctas--card">' +
          '<a class="pes-oos-btn pes-oos-btn--call" href="tel:888-876-0007" aria-label="Call Portlandia Electric Supply about ' + esc(hit.title) + '">Call Now</a>' +
          '<a class="pes-oos-btn pes-oos-btn--email" href="/pages/request-quote?product=' + esc(hit.handle) + '&name=' + encodeURIComponent(hit.title) + '" aria-label="Email Portlandia Electric Supply for a quote on ' + esc(hit.title) + '">Email Us</a>' +
        '</div>'
      : '';

    return '<li class="grid__item">' +
      '<div class="search-card-stock-wrap" style="position:relative;">' +
        '<div class="card-wrapper product-card-wrapper underline-links-hover">' +
          '<div class="card card--standard card--media" style="--ratio-percent: 100.0%;">' +
            '<div class="card__inner color-scheme-2 gradient ratio" style="--ratio-percent: 100.0%;">' +
              '<div class="card__media"><div class="media media--transparent media--hover-effect">' + img + '</div></div>' +
              '<div class="card__content"><div class="card__information">' +
                '<h3 class="card__heading"><a href="' + esc(url) + '" id="StandardCardNoMediaLink--' + esc(hit.id) + '" class="full-unstyled-link col_prod_2" aria-labelledby="StandardCardNoMediaLink--' + esc(hit.id) + '">' + esc(hit.title) + '</a></h3>' +
              '</div><div class="card__badge bottom left"></div></div>' +
            '</div>' +
            '<div class="card__content">' +
              '<div class="card__information">' +
                '<span class="visually-hidden">Vendor:</span>' +
                '<div class="pes-card-brand">' + esc(hit.vendor || '') + '</div>' +
                '<div><h3 class="card__heading h5" id="title--' + esc(hit.id) + '">' +
                  '<a href="' + esc(url) + '" title="' + esc(hit.title) + '" id="CardLink--' + esc(hit.id) + '" class="full-unstyled-link col_prod" aria-labelledby="CardLink--' + esc(hit.id) + '">' + esc(hit.title) + '</a>' +
                '</h3></div>' +
                '<div class="productCardInnerWrap"><div class="card-information">' +
                  '<span class="caption-large light"></span>' +
                  '<div class="jdgm-widget jdgm-preview-badge"><div style="display:none" class="jdgm-prev-badge" data-average-rating="0.00" data-number-of-reviews="0" data-number-of-questions="0"></div></div>' +
                  priceHtml(hit) +
                  '<div class="pes-stock-state pes-stock-state--' + s.state + '">' +
                    '<span class="pes-stock-dot" aria-hidden="true"></span><span>' + esc(s.cardLabel) + '</span>' +
                  '</div>' +
                  (s.ship ? '<div class="pes-card-ship">' + esc(s.ship) + '</div>' : '') +
                '</div></div>' +
              '</div>' +
              badge +
            '</div>' +
          '</div>' +
        '</div>' +
        '<span class="search-stock-badge search-stock-badge--' + s.state + '">' + esc(s.corner) + '</span>' +
        oosCtas +
      '</div></li>';
  }

  function pagerHtml(q, page, total) {
    var pages = Math.min(Math.ceil(total / PAGE_SIZE), Math.ceil(5000 / PAGE_SIZE));
    if (pages <= 1) return '';
    var items = '';
    function pageLink(n, label, cls, extra) {
      return '<li><a href="/search?q=' + encodeURIComponent(q) + '&page=' + n + '" class="pagination__item ' + (cls || 'link') + '" aria-label="Page ' + n + '"' + (extra || '') + '>' + label + '</a></li>';
    }
    if (page > 1) {
      items += '<li><a href="/search?q=' + encodeURIComponent(q) + '&page=' + (page - 1) + '" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Previous page"><span class="svg-wrapper">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" style="transform:scaleX(-1)"><path d="M5.27102 1.60402L15.555 12L5.27102 22.396L6.87102 23.978L18.72 12L6.87002 0.0200195L5.27002 1.60302L5.27102 1.60402Z" fill="black"/></svg>' +
        '</span></a></li>';
    }
    var from = Math.max(1, page - 2);
    var to = Math.min(pages, page + 2);
    if (from > 1) { items += pageLink(1, '1'); if (from > 2) items += '<li><span class="pagination__item">&hellip;</span></li>'; }
    for (var n = from; n <= to; n++) {
      if (n === page) {
        items += '<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ' + n + '">' + n + '</a></li>';
      } else {
        items += pageLink(n, String(n));
      }
    }
    if (to < pages) { if (to < pages - 1) items += '<li><span class="pagination__item">&hellip;</span></li>'; items += pageLink(pages, String(pages)); }
    if (page < pages) {
      items += '<li><a href="/search?q=' + encodeURIComponent(q) + '&page=' + (page + 1) + '" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Next page"><span class="svg-wrapper">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5.27102 1.60402L15.555 12L5.27102 22.396L6.87102 23.978L18.72 12L6.87002 0.0200195L5.27002 1.60302L5.27102 1.60402Z" fill="black"/></svg>' +
        '</span></a></li>';
    }
    return '<div class="pagination-wrapper" data-page="' + page + '"><nav class="pagination" role="navigation" aria-label="Pagination">' +
      '<ul class="pagination__list list-unstyled" role="list">' + items + '</ul></nav></div>';
  }

  function renderSearchPage(q, res, page) {
    var hits = res.hits || [];
    var total = res.estimatedTotalHits || hits.length;

    var grid = document.querySelector('#product-grid ul.product-grid') ||
               document.querySelector('ul#product-grid.product-grid') ||
               document.querySelector('#product-grid ul');

    if (!grid) {
      // Shopify rendered the empty template — build a grid where it would be.
      var root = document.querySelector('.template-search');
      if (!root) return;
      root.classList.remove('template-search--empty');
      var statusP = root.querySelector('.template-search__header p[role="status"]');
      if (statusP) statusP.style.display = 'none';
      var zeroSlot = root.querySelector('[data-pes-search-expansion]');
      if (zeroSlot) zeroSlot.style.display = 'none';
      var container = document.createElement('div');
      container.className = 'product-grid-container';
      container.id = 'ProductGridContainer';
      container.innerHTML = '<div class="template-search__results collection page-width" id="product-grid">' +
        '<div class="loading-overlay gradient"></div>' +
        '<ul class="grid product-grid grid--2-col-tablet-down grid--4-col-desktop" role="list"></ul>' +
        '</div>';
      root.appendChild(container);
      grid = container.querySelector('ul');
    }

    var cards = '';
    hits.forEach(function (h) { cards += cardHtml(h); });
    grid.innerHTML = cards;

    // count line(s)
    var headerStatus = document.querySelector('.template-search__header p[role="status"]');
    if (headerStatus && headerStatus.style.display !== 'none') {
      headerStatus.textContent = total + ' results for \u201C' + q + '\u201D';
    }
    var pcd = document.getElementById('ProductCountDesktop');
    if (pcd) pcd.textContent = total + (total === 1 ? ' product' : ' products');

    // hide Shopify-native sort select (its ordering no longer applies)
    var sortField = document.querySelector('#FacetSortForm .facet-filters__field');
    if (sortField) sortField.style.display = 'none';
    // hide native facets sidebar (reflects Shopify's result set, not Riven's)
    var facets = document.getElementById('main-search-filters');
    if (facets) facets.style.display = 'none';

    // pagination: replace native with Riven pager
    var nativePager = document.querySelector('.pagination-wrapper');
    var newPager = pagerHtml(q, page, total);
    if (nativePager) {
      if (newPager) {
        var tmp = document.createElement('div');
        tmp.innerHTML = newPager;
        nativePager.parentNode.replaceChild(tmp.firstChild, nativePager);
      } else {
        nativePager.style.display = 'none';
      }
    } else if (newPager) {
      var gridContainer = document.getElementById('product-grid');
      if (gridContainer) gridContainer.insertAdjacentHTML('beforeend', newPager);
    }

    document.title = document.title; // no-op; keep head untouched (Clarity/GTM intact)
  }

  function runSearchPage() {
    if (!/^\/search\/?$/.test(location.pathname)) return;
    var params = new URLSearchParams(location.search);
    var q = (params.get('q') || '').trim();
    if (!q) return;
    var page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);

    rivenQuery(q, PAGE_SIZE, (page - 1) * PAGE_SIZE).then(function (res) {
      if (!res || !res.hits || !res.hits.length) return; // keep native page
      resolveImages(res.hits).then(function () {
        // user may have navigated; confirm still same query page
        var stillQ = (new URLSearchParams(location.search).get('q') || '').trim();
        if (stillQ !== q) return;
        renderSearchPage(q, res, page);
      });
    });
  }

  /* ---------- boot ---------- */

  function boot() {
    injectStyles();
    var tries = 0;
    (function ensure() {
      if (patchPredictive() || ++tries > 40) return;
      setTimeout(ensure, 100);
    })();
    runSearchPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
